import React, { useRef, useEffect, useCallback } from "react";
import "./scroll-picker.scss";

interface ScrollPickerItem {
  label: string;
  value: string | number;
}

interface ScrollPickerProps {
  items: ScrollPickerItem[];
  selectedValue?: string | number;
  onSelect: (value: string | number) => void;
  onClose: () => void;
}

const ITEM_HEIGHT = 32;

export function ScrollPicker({
  items,
  selectedValue,
  onSelect,
  onClose,
}: ScrollPickerProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Scroll the selected item into view on mount
  useEffect(() => {
    if (!listRef.current) return;
    const idx = items.findIndex((it) => it.value === selectedValue);
    if (idx >= 0) {
      listRef.current.scrollTo({
        top: Math.max(0, idx * ITEM_HEIGHT - ITEM_HEIGHT * 2),
        behavior: "auto",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        overlayRef.current &&
        !overlayRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleItemClick = useCallback(
    (item: ScrollPickerItem) => {
      onSelect(item.value);
      onClose();
    },
    [onSelect, onClose]
  );

  return (
    <div
      className="scroll-picker-overlay"
      ref={overlayRef}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="scroll-picker" ref={listRef}>
        {items.map((item) => (
          <div
            key={item.value}
            className={`scroll-picker__item${
              item.value === selectedValue
                ? " scroll-picker__item--selected"
                : ""
            }`}
            onClick={() => handleItemClick(item)}
          >
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
}
